// Account on your own server (/api): "Continue with Google", plan & expiry, and a
// check-in when the app opens so the admin panel knows who uses which version.
// Only account details and counts are sent – never invoices, clients or documents.
// Every call fails quietly: if the server is down the app keeps working offline.

const API = (process.env.NEXT_PUBLIC_API_BASE || "/api").replace(/\/+$/, "");
const ACCOUNT_KEY = "invoice-studio-account";
const INSTALL_KEY = "invoice-studio-install-id";
const CONFIG_KEY = "invoice-studio-server-config";
const PING_KEY = "invoice-studio-last-ping";

const read = (k) => {
  try {
    return JSON.parse(localStorage.getItem(k) || "null");
  } catch {
    return null;
  }
};
const write = (k, v) => {
  try {
    if (v == null) localStorage.removeItem(k);
    else localStorage.setItem(k, JSON.stringify(v));
  } catch {
    /* ignore */
  }
};

export function installId() {
  let id = read(INSTALL_KEY);
  if (!id) {
    id = crypto.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    write(INSTALL_KEY, id);
  }
  return id;
}

async function call(path, body, { token, timeout = 8000 } = {}) {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), timeout);
  try {
    const res = await fetch(API + path, {
      method: body ? "POST" : "GET",
      headers: { ...(body ? { "Content-Type": "application/json" } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: body ? JSON.stringify(body) : undefined,
      signal: ctl.signal,
    });
    const j = await res.json().catch(() => null);
    // an HTML page instead of JSON means /api/ isn't routed to the account server
    if (!j) throw new Error("Couldn’t reach the account server – you can keep working offline.");
    if (!res.ok) {
      const e = new Error(j.error || `Server error ${res.status}`);
      e.status = res.status;
      throw e;
    }
    return j;
  } catch (e) {
    if (e.name === "AbortError") throw new Error("The account server didn’t answer – check your internet connection.");
    if (e instanceof TypeError) throw new Error("Couldn’t reach the account server – you can keep working offline.");
    throw e;
  } finally {
    clearTimeout(t);
  }
}

// Google OAuth client ID: set in the admin panel and published by the server
// (a build-time value is only a fallback).
export function googleClientId() {
  return read(CONFIG_KEY)?.googleClientId || process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || "";
}
export const serverConfig = () => read(CONFIG_KEY) || {};
export async function loadServerConfig() {
  try {
    const c = await call("/config", null, { timeout: 5000 });
    write(CONFIG_KEY, c);
    return c;
  } catch {
    return read(CONFIG_KEY) || {};
  }
}

export const getAccount = () => read(ACCOUNT_KEY);
export const signOutAccount = () => write(ACCOUNT_KEY, null);

// What the admin panel shows about this workspace.
function profileOf(store) {
  const b = store?.defaultBrand || {};
  return { business: b.name, phone: b.phone, city: b.city, state: b.state, country: b.country, industry: b.industry, gstin: b.gstin };
}
function statsOf(store) {
  const inv = store?.invoices || [];
  return { invoices: inv.filter((i) => (i.docType || "invoice") === "invoice").length, quotes: inv.filter((i) => i.docType === "quote").length, clients: (store?.clients || []).length, letters: (store?.letters || []).length };
}

// Sign in with a Google ID token. Returns { account, vaultKey, sub, token }.
export async function signInWithGoogle(credential, store) {
  const r = await call("/auth/google", { credential, installId: installId(), version: process.env.NEXT_PUBLIC_APP_VERSION, profile: store ? profileOf(store) : undefined, stats: store ? statsOf(store) : undefined });
  write(ACCOUNT_KEY, { token: r.token, account: r.account, sub: r.sub, at: new Date().toISOString() });
  return r;
}

// Check in (at most every 6 hours). Never throws.
export async function checkIn(store, { force = false } = {}) {
  loadServerConfig(); // keep the Client ID & support contact fresh
  try {
    const last = Number(read(PING_KEY) || 0);
    if (!force && Date.now() - last < 6 * 3600e3) return getAccount();
    const acc = getAccount();
    const r = await call("/ping", { installId: installId(), version: process.env.NEXT_PUBLIC_APP_VERSION, profile: profileOf(store), stats: statsOf(store) }, { token: acc?.token });
    write(PING_KEY, Date.now());
    if (acc && r.account) write(ACCOUNT_KEY, { ...acc, account: r.account });
    if (acc && !r.account) write(ACCOUNT_KEY, null); // session no longer valid
    return getAccount();
  } catch {
    return getAccount();
  }
}
